import {
  Activity,
  Archive,
  ArrowRight,
  ArrowUpRight,
  AtSign,
  BellRing,
  Bookmark,
  CalendarClock,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CirclePause,
  Clapperboard,
  Clock,
  Copy,
  CornerDownLeft,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  GalleryHorizontal,
  Gauge,
  GitBranch,
  Grid2x2,
  Hash,
  Heart,
  Hourglass,
  Image,
  ImagePlus,
  Inbox,
  Layers,
  LayoutPanelLeft,
  Link,
  Lock,
  Megaphone,
  MessageCircle,
  MessageSquareReply,
  MessageSquareText,
  MessagesSquare,
  Mic,
  Moon,
  MousePointerClick,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Reply,
  RotateCw,
  Search,
  SearchX,
  Send,
  Settings,
  Share2,
  ShieldCheck,
  Smile,
  Sparkles,
  SquarePen,
  Sun,
  Timer,
  Trash2,
  TrendingUp,
  TriangleAlert,
  UserCheck,
  Users,
  Video,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";

/**
 * The panel's icons, by the Lucide name the design system uses.
 *
 * They used to be masks over SVGs downloaded from unpkg on every visit; now they are part of
 * the bundle and only the ones listed here are shipped.
 */
const ICONS = {
  activity: Activity,
  archive: Archive,
  "arrow-right": ArrowRight,
  "arrow-up-right": ArrowUpRight,
  "at-sign": AtSign,
  "bell-ring": BellRing,
  bookmark: Bookmark,
  "calendar-clock": CalendarClock,
  "chart-column": ChartColumn,
  check: Check,
  "check-circle-2": CircleCheck,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "circle-alert": CircleAlert,
  "circle-dashed": CircleDashed,
  clapperboard: Clapperboard,
  clock: Clock,
  copy: Copy,
  "corner-down-left": CornerDownLeft,
  download: Download,
  ellipsis: Ellipsis,
  "external-link": ExternalLink,
  eye: Eye,
  "gallery-horizontal": GalleryHorizontal,
  gauge: Gauge,
  "git-branch": GitBranch,
  "grid-2x2": Grid2x2,
  hash: Hash,
  heart: Heart,
  hourglass: Hourglass,
  image: Image,
  "image-plus": ImagePlus,
  inbox: Inbox,
  layers: Layers,
  "layout-panel-left": LayoutPanelLeft,
  link: Link,
  lock: Lock,
  megaphone: Megaphone,
  "message-circle": MessageCircle,
  "message-square-reply": MessageSquareReply,
  "message-square-text": MessageSquareText,
  "messages-square": MessagesSquare,
  mic: Mic,
  moon: Moon,
  "mouse-pointer-click": MousePointerClick,
  "pause-circle": CirclePause,
  pencil: Pencil,
  play: Play,
  plus: Plus,
  "refresh-cw": RefreshCw,
  reply: Reply,
  "rotate-cw": RotateCw,
  search: Search,
  "search-x": SearchX,
  send: Send,
  settings: Settings,
  "share-2": Share2,
  "shield-check": ShieldCheck,
  smile: Smile,
  sparkles: Sparkles,
  "square-pen": SquarePen,
  sun: Sun,
  timer: Timer,
  "trash-2": Trash2,
  "trending-up": TrendingUp,
  "triangle-alert": TriangleAlert,
  "user-check": UserCheck,
  users: Users,
  video: Video,
  x: X,
  zap: Zap,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

export function isIconName(name: string): name is IconName {
  return name in ICONS;
}

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  className?: string;
}

/** Decorative by default: every icon in the panel sits next to the text that names it. */
export function Icon({ name, size = 18, color = "currentColor", className }: IconProps) {
  const Glyph = ICONS[name];
  return <Glyph size={size} color={color} strokeWidth={2} className={className} aria-hidden="true" />;
}
